import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import { useTheme } from '../../DataStore/ThemeContext';
import { useReport } from '../../DataStore/ReportContext';
import functions from '../../util/functions';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import printContent, { numberFormat } from '../../util/araknerdlib';
import AlertDateSelector from '../../components/Report/AlertDateSelector';
import LetterHead from '../../components/Securities/LetterHead';
import Signatory from '../../components/Securities/Signatory';
import { useCurrency } from '../../util/SystemVariables';

const BalanceSheetPage = () => {

    const {currentNotify} = useTheme();
    const {getBalanceSheetReport, balanceSheetReport, dispatch, BSDetailLevel} = useReport();
    const statementRef = useRef(); // Ref to enclose statement content for printing
    const [showAlert, setShowAlert] = useState(false);
    const currency = useCurrency();
    

    // Fetch trial balance data whenever the level changes
    useEffect(() => {
        getBalanceSheetReport()
    }, []);

    const handleReset = ()=>{
        dispatch({ type: 'BSDetailLevel', payload: 0 });
    }

    const reduceDetail = ()=>{
        dispatch({ type: 'BSDetailLevel', payload:  BSDetailLevel + 1 });
    }

    const handleConfirmDateRange = async (range) => {
        dispatch({ type: 'dateRange', payload: range });
        await getBalanceSheetReport(true, range);
      };

    return (
        <>
        <PageContainer>
            <MainContent>
                {/* Page Title */}
                <PageTitle
                    title="Balance Sheet"
                    description="Company's financial statement that provides a snapshot of the company's financial position at a particular date."
                    iconClass="fas fa-chart-line"
                    sticky={true}
                    PageActions={() => (
                        <>
                            <ActionDropdownItem iconClass="fa fa-calendar-alt text-success" label="Filter Date Range" onClick={()=>setShowAlert(true)}/>
                            <ActionDropdownItem iconClass="fas fa-search-plus text-danger" label="Reduce Detail Level ..." onClick={reduceDetail}/>
                            <ActionDropdownItem iconClass="fa fas fa-binoculars text-info" label="Reset Detail Level" onClick={handleReset}/>
                            <ActionDropdownItem iconClass="fa fa-print" label="Print Statement" onClick={()=>printContent(statementRef, "Balance Sheet")}/>
                            <ActionDropdownItem iconClass="fa fa-sync text-success" label="Refresh" onClick={()=>getBalanceSheetReport(true)} />
                        </>
                    )}
                />
                {currentNotify}
                
                 {/* Statement Content */}
                 <div ref={statementRef}>
                    <LetterHead />
                    <div className="content-wrapper d-flex">
                        {/* Statement Header */}
                        <div className="header-section" style={{ flex: 2 }}>
                            <h4>Balance Sheet</h4>
                            <p>Snapshot of company's financial position <br/>

                                <strong>Currency:</strong> {currency || 'N/A'}
                            </p>

                            {functions.isObject(balanceSheetReport) && balanceSheetReport.summary && ( <p>
                                <strong>Detail Level: </strong> {balanceSheetReport.summary.level} <br/>
                                <strong>Financial Period: </strong>{' '}
                                {balanceSheetReport.summary.opening_date && <>{functions.formatDate(balanceSheetReport.summary.opening_date)} - {functions.formatDate(balanceSheetReport.summary.closing_date)}</>}
                            </p>)}
                        </div>

                        {/* Statement Summary */}
                        {functions.isObject(balanceSheetReport) && balanceSheetReport.summary && (
                            <div className="summary-table" style={{ flex: 1 }}>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Total Assets</th>
                                            <td>{numberFormat(balanceSheetReport.totals?.total_assets || 0)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Equity & Liabilities</th>
                                            <td>{numberFormat(balanceSheetReport.totals?.total_liabilities || 0 )}</td>
                                        </tr>
                                        <tr>
                                            <th>Status</th>
                                            <td>{balanceSheetReport.summary.comment}</td>

                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="card mb-4">
                        <div className="card-body">
                        <div className="table-responsive">
                        {functions.isObject(balanceSheetReport) && <><table className="table table-bordered">
                                <tbody>
                                   <tr>
                                        <th className="font-weight-bold bg-light">Assets</th>
                                        <th className="font-weight-bold bg-light" style={{textAlign:'right'}}>{currency}</th>
                                        <th className="font-weight-bold bg-light" style={{textAlign:'right'}}>{currency}</th>

                                    </tr>
                                    {balanceSheetReport.balance_sheet.Assets.map((item) => (
                                        <tr key={item.account_id}>
                                            <td>{item.account_name}</td>
                                            <td className="text-right">
                                            {numberFormat(item?.net_balance || 0)}
                                            </td>
                                            <td>&nbsp;</td>
                                        </tr>
                                    ))}
                                    <tr className="font-weight-bold">
                                        <th style={{textAlign:'right'}}>Total Assets</th>
                                        <th>&nbsp;</th>
                                        <th className="text-right text-dark">
                                            {numberFormat(balanceSheetReport.totals?.total_assets || 0)}
                                        </th>
                                    </tr>
    
                                    <tr>
                                        <th colSpan="3" className="font-weight-bold bg-light">
                                            Equity & Liabilities
                                        </th>
                                    </tr>

                                    {balanceSheetReport.balance_sheet.Liabilities.map((item) => (
                                        <tr key={item.account_id}>
                                            <td>{item.account_name}</td>
                                            <td className="text-right">
                                            {numberFormat(item?.net_balance || 0)}
                                            </td>
                                            <td>&nbsp;</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td>{balanceSheetReport.totals.net_income > 0 ? 'Net Income' : 'Net Loss'}</td>
                                        <td className="text-right">
                                            {numberFormat(balanceSheetReport.totals?.net_income || 0)}
                                        </td>
                                        <td>&nbsp;</td>
                                    </tr>
                                    <tr className="font-weight-bold">
                                        <th style={{textAlign:'right'}}>Total Equity & Liabilities</th>
                                        <th>&nbsp;</th>
                                        <th className="text-right text-dark">
                                            {functions.numberFormat(balanceSheetReport.totals.total_liabilities)}
                                        </th>
                                    </tr>
    
                                </tbody>
                        
                            </table>
                            <Signatory />
                            </>}
                            {!balanceSheetReport && <ActivityLoader />}
                            {balanceSheetReport === "404" && <Notification message="Balance sheet could not be loaded at this time"/>}
                            </div>
                        </div>
                    </div>


                        
                       


                    </div>

            </MainContent>
        </PageContainer>
        <AlertDateSelector
            show={showAlert}
            onClose={() => setShowAlert(false)}
            onConfirm={handleConfirmDateRange}
        />
        </>
    );
};

export default BalanceSheetPage;
